import {
    Divider,
    Heading,
    List,
    Skeleton,
    TabPanel,
    TabPanels,
    Tabs,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useArticleStocks } from "api/api-hooks/use-article-stocks";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import PreorderMenuGroup from "components/selection-components/preorder/preorder-menu-group";
import TabListComponent, {
    TabListHandle,
} from "components/selection-components/preorder/tablist-component";
import { PreorderContext } from "context/preorder-context";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookingMenu, MenuStatus } from "types/menu";
import { MenuGroup } from "types/menu-group";
import testId from "types/test-id";
import { TimeSlot } from "types/time-slot";
interface IPreorderBody {
    selectedTime?: TimeSlot;
    preorderHeaderName?: string;
    blockInitialQuantities?: boolean;
}

const PreorderBody = ({
    selectedTime,
    preorderHeaderName,
    blockInitialQuantities = false,
}: IPreorderBody) => {
    const { t } = useTranslation();
    const tabListRef = useRef<TabListHandle | null>(null);

    const { data: reservation } = useWebBooking();
    const { data: articleStocks } = useArticleStocks();
    const { currentQuantities, setCurrentQuantities, activeTabIndex } =
        useContext(PreorderContext) || {};

    const [bookingMenu, setBookingMenu] = useState<BookingMenu | null>(null);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const { updateGroupValidity, groupValidityMap, setGroupNames } =
        useContext(PreorderContext) || {};
    const { data: settings } = useExternalBookingSettings();

    // Get only business groups for quantity tracking
    const getBusinessGroups = (menu: BookingMenu | null) => {
        return menu?.groups || [];
    };

    // Get UI groups for display (includes info group) - memoized for performance
    const uiGroups = useMemo(() => {
        const businessGroups = getBusinessGroups(bookingMenu);
        const groups: MenuGroup[] = [];

        // Add info group if we have a menu message
        if (settings?.menuMessage) {
            groups.push({
                name: t("preorder.menuInformation"),
                groupType: 0,
                items: [
                    {
                        name: t("preorder.menuInformation"),
                        description: settings.menuMessage,
                        webFlags: 2,
                    },
                ],
            } as MenuGroup);
        }

        // Add business groups
        groups.push(...businessGroups);
        return groups;
    }, [bookingMenu, settings?.menuMessage, t]);

    useEffect(() => {
        if (!bookingMenu) {
            // For a simple booking, we can use the any article to find the menu
            let bookedResource = reservation?.articles.at(0);

            if (selectedTime) {
                // For a activity-booking, we will retrive the menu matching the selected time start
                bookedResource = reservation?.articles.find((activity) =>
                    activity.start.isSame(selectedTime?.start)
                );
            }
            const bookingMenu = reservation?.menus?.find(
                (menu) =>
                    menu.status === MenuStatus.Sent && menu.publicId === bookedResource?.menuGuid
            );

            // Fixed initialQuantities to handle undefined group.items by ensuring empty arrays
            const businessGroups = getBusinessGroups(bookingMenu || null);
            const initialQuantities =
                businessGroups.map(
                    (group) =>
                        // If group.items is undefined, return an empty array instead
                        group.items?.map((item) => item.quantity) || []
                ) || [];
            if (!blockInitialQuantities) setCurrentQuantities?.(initialQuantities);
            setBookingMenu(bookingMenu ? bookingMenu : null);
        }
    }, [
        reservation,
        setCurrentQuantities,
        blockInitialQuantities,
        selectedTime?.start,
        bookingMenu,
        selectedTime,
        settings?.menuMessage,
        t,
    ]);

    useEffect(() => {
        if (bookingMenu) {
            setGroupNames?.(bookingMenu?.groups?.map((group) => group.name) ?? []);
        } else {
            setGroupNames?.([]);
        }
    }, [bookingMenu, setGroupNames]);

    useEffect(() => {
        if (activeTabIndex !== undefined) {
            setSelectedTabIndex(activeTabIndex);
            if (tabListRef.current) {
                tabListRef.current.scrollTabIntoView(activeTabIndex);
            }
        }
    }, [activeTabIndex]);

    const headerText = t("preorder.headerText", { headerName: preorderHeaderName });

    if (!bookingMenu || !bookingMenu.groups) {
        return null;
    }

    // Prepare tab items for the TabListComponent. Highlight invalid tabItems
    const tabItems =
        uiGroups?.map((group, displayIdx) => {
            // Find the business index for validation highlighting
            const businessIdx = getBusinessGroups(bookingMenu).findIndex(
                (bg) => bg.name === group.name && bg.items === group.items
            );

            return {
                id: displayIdx,
                name: group.name,
                highlighted: businessIdx >= 0 ? groupValidityMap?.[businessIdx] === false : false,
            };
        }) ?? [];

    const handleTabChange = (index: number) => {
        setSelectedTabIndex(index);

        if (tabListRef.current) {
            tabListRef.current.scrollTabIntoView(index);
        }
    };

    return (
        <Tabs
            data-testid={testId.preorder.wrapper}
            index={selectedTabIndex}
            onChange={handleTabChange}
        >
            {!preorderHeaderName ? (
                <Skeleton
                    m={`0 ${ThemeSpaceVariable.Medium} ${ThemeSpaceVariable.Medium} ${ThemeSpaceVariable.Medium}`}
                    w="450px"
                    h="32px"
                />
            ) : (
                <Heading
                    as="h2"
                    fontFamily={ThemeFontVariable.SubHeader}
                    fontWeight="medium"
                    p={`0 ${ThemeSpaceVariable.Medium} ${ThemeSpaceVariable.Medium} ${ThemeSpaceVariable.Medium}`}
                >
                    {headerText}
                </Heading>
            )}

            <TabListComponent ref={tabListRef} items={tabItems} onTabChange={handleTabChange} />

            <Divider marginBottom={ThemeSpaceVariable.Medium} />

            <TabPanels marginTop={ThemeSpaceVariable.Large}>
                {uiGroups.map((menuGroup, displayIdx) => {
                    // Find the business index for this group (if it's not an info group)
                    const businessIdx = getBusinessGroups(bookingMenu).findIndex(
                        (bg) => bg.name === menuGroup.name && bg.items === menuGroup.items
                    );
                    const isInfoGroup = menuGroup.name === t("preorder.menuInformation");

                    return (
                        <TabPanel key={`tabpanel-${displayIdx}`}>
                            <List
                                gap={ThemeSpaceVariable.Medium}
                                p={`0 ${ThemeSpaceVariable.Medium} 0 ${ThemeSpaceVariable.Medium}`}
                            >
                                <PreorderMenuGroup
                                    menuGroup={menuGroup}
                                    currentGroupQuantities={
                                        businessIdx >= 0
                                            ? (currentQuantities?.[businessIdx] ?? [])
                                            : []
                                    }
                                    guests={reservation?.guests}
                                    articleStocks={articleStocks}
                                    setGroupQuantities={(groupQuantities: number[]) => {
                                        if (businessIdx >= 0) {
                                            setCurrentQuantities?.(
                                                (currentQuantities ?? []).toSpliced(
                                                    businessIdx,
                                                    1,
                                                    groupQuantities
                                                ) ?? []
                                            );
                                        }
                                    }}
                                    onValidationChange={(isValid: boolean) => {
                                        if (businessIdx >= 0) {
                                            updateGroupValidity?.(businessIdx, isValid);
                                        }
                                    }}
                                    isInfoGroup={isInfoGroup}
                                />
                            </List>
                        </TabPanel>
                    );
                })}
            </TabPanels>
        </Tabs>
    );
};

export default PreorderBody;
